import { useState, useEffect, useRef, useCallback } from 'react';
import {
  productService, categoryService, uploadService, resolverImagemUrl,
  estoqueService, Product, Category, MovimentacaoEstoque,
} from '../../services';
import { usePagination } from '../../hooks/usePagination';
import PaginationControls from '../../components/ui/PaginationControls';
import PaginatedContent from '../../components/ui/PaginatedContent';
import ModalConfirmacao from '../../components/ModalConfirmacao';

const fmtDinheiro = (v: string | number) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** Redimensiona a imagem para no máximo `maxLado` px e devolve data URL */
const redimensionarImagem = (arquivo: File, maxLado: number, qualidade: number) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const escala = Math.min(1, maxLado / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(img.width * escala));
        canvas.height = Math.max(1, Math.round(img.height * escala));
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('canvas'));
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const mime =
          arquivo.type === 'image/png' || arquivo.type === 'image/webp' ? arquivo.type : 'image/jpeg';
        resolve(canvas.toDataURL(mime, qualidade));
      };
      img.onerror = () => reject(new Error('imagem inválida'));
      img.src = String(reader.result);
    };
    reader.onerror = () => reject(new Error('leitura'));
    reader.readAsDataURL(arquivo);
  });

export default function Products() {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [enviandoImagem, setEnviandoImagem] = useState(false);
  // T117/T123: erro de ação aparece na tela (não em alert)
  const [erroSalvar, setErroSalvar] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Histórico do produto (modal)
  const [produtoHistorico, setProdutoHistorico] = useState<Product | null>(null);
  // Movimentações crescem com o tempo — paginadas no backend
  const movsPg = usePagination({ defaultLimit: 10 });
  const {
    page: movPage, limit: movLimit, totalItems: movTotal, totalPages: movTotalPages,
    setPage: setMovPage, setLimit: setMovLimit, syncFromResponse: syncMovs,
  } = movsPg;
  const [movsRefresh, setMovsRefresh] = useState(0);
  const [movs, setMovs] = useState<MovimentacaoEstoque[]>([]);
  const [baixaForm, setBaixaForm] = useState({ quantidade: '1', motivo: 'Perda' });
  const [baixando, setBaixando] = useState(false);
  // Paginação inteligente (padrão do projeto — busca server-side no banco)
  const {
    page, limit, filters, totalItems, totalPages,
    setPage, setLimit, setFilters, buildQueryParams, syncFromResponse,
  } = usePagination({ defaultLimit: 20, defaultFilters: { busca: '' } });
  // Digitação instantânea; a busca é aplicada com debounce (350ms)
  const [buscaDigito, setBuscaDigito] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setFilters({ busca: buscaDigito }), 350);
    return () => clearTimeout(timer);
  }, [buscaDigito, setFilters]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    image_url: '',
    category_id: '',
    exige_cozinha: true,
    tipo: 'PRODUTO' as 'PRODUTO' | 'INSUMO',
    exibir_cardapio: true,
    controlar_estoque: false,
    unidade: 'un',
    consumo_por_venda: '1',
    estoque_minimo: '0',
    receita: [] as Array<{ insumo_id: string; quantidade: string }>,
  });

  const formDataVazio = {
    name: '',
    description: '',
    price: '',
    image_url: '',
    category_id: '',
    exige_cozinha: true,
    tipo: 'PRODUTO' as 'PRODUTO' | 'INSUMO',
    exibir_cardapio: true,
    controlar_estoque: false,
    unidade: 'un',
    consumo_por_venda: '1',
    estoque_minimo: '0',
    receita: [] as Array<{ insumo_id: string; quantidade: string }>,
  };

  // Recarrega só a lista de produtos (usada após criar/editar/excluir/baixa)
  const buscarProdutos = useCallback(async () => {
    try {
      const params = Object.fromEntries(buildQueryParams()) as {
        page?: number;
        limit?: number;
        busca?: string;
      };
      const productsRes = await productService.findAll(params);
      setProducts(productsRes.data.data);
      syncFromResponse(productsRes.data);
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
    }
  }, [buildQueryParams, syncFromResponse]);

  useEffect(() => {
    let cancelado = false;
    setIsLoading(true);
    (async () => {
      try {
        const [, categoriesRes] = await Promise.all([
          buscarProdutos(),
          categoryService.findAll({ page: 1, limit: 100 }),
        ]);
        if (cancelado) return;
        setCategories(categoriesRes.data.data);
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      } finally {
        if (!cancelado) setIsLoading(false);
      }
    })();
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, filters, buscarProdutos]);

  /** Monta o payload do formulário (T123: `confirmar_duplicado` quando o dono aceita repetir) */
  const montarPayloadProduto = (confirmarDuplicado: boolean) => {
    const ehInsumo = formData.tipo === 'INSUMO';
    return {
      ...formData,
      price: parseFloat(formData.price),
      receita: undefined,
      consumo_por_venda: Number(formData.consumo_por_venda || 1),
      estoque_minimo: Number(formData.estoque_minimo || 0),
      // Insumo: nunca vai ao cardápio e sempre controla estoque (regra no servidor também)
      exibir_cardapio: ehInsumo ? false : formData.exibir_cardapio,
      controlar_estoque: ehInsumo ? true : formData.controlar_estoque,
      confirmar_duplicado: confirmarDuplicado || undefined,
    };
  };

  /** Salva produto/insumo + ficha técnica (usado no envio normal e no confirmado) */
  const salvarProduto = async (confirmarDuplicado: boolean) => {
    const ehInsumo = formData.tipo === 'INSUMO';
    const data = montarPayloadProduto(confirmarDuplicado);

    let idProduto = editingProduct?.id;

    if (editingProduct) {
      await productService.update(editingProduct.id, data);
    } else {
      const criado = await productService.create(data);
      idProduto = criado.data.data.id;
    }

    // Receita do prato: ingredientes que consomem estoque em cada venda
    if (idProduto && !ehInsumo) {
      const itens = formData.receita
        .filter((r) => r.insumo_id && Number(r.quantidade) > 0)
        .map((r) => ({ insumo_id: r.insumo_id, quantidade: Number(r.quantidade) }));
      await productService.setRecipe(idProduto, itens);
    }

    setShowForm(false);
    setEditingProduct(null);
    setFormData(formDataVazio);
    await buscarProdutos();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErroSalvar('');

    // T123: nome repetido é avisado pelo servidor e só entra se o dono confirmar
    try {
      await salvarProduto(false);
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      const msg = (error as { response?: { data?: { message?: string } } })?.response?.data
        ?.message;

      if (status === 409 && msg?.includes('Já existe')) {
        if (confirm(`${msg}\n\nCadastrar mesmo assim, com o nome repetido?`)) {
          try {
            await salvarProduto(true);
          } catch (erroConfirmado) {
            const msg2 = (erroConfirmado as { response?: { data?: { message?: string } } })
              ?.response?.data?.message;
            setErroSalvar(msg2 || 'Não foi possível salvar o produto.');
          }
        } else {
          setErroSalvar(msg);
        }
        return;
      }

      console.error('Erro ao salvar produto:', error);
      setErroSalvar(msg || 'Erro ao salvar produto. Tente novamente.');
    }
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description || '',
      price: String(product.price),
      image_url: product.image_url || '',
      category_id: product.category_id,
      exige_cozinha: product.exige_cozinha,
      tipo: product.tipo === 'INSUMO' ? 'INSUMO' : 'PRODUTO',
      exibir_cardapio: product.exibir_cardapio,
      controlar_estoque: product.controlar_estoque,
      unidade: product.unidade || 'un',
      consumo_por_venda: String(product.consumo_por_venda ?? 1),
      estoque_minimo: String(product.estoque_minimo ?? 0),
      receita: (product.receita ?? []).map((r) => ({
        insumo_id: r.insumo_id,
        quantidade: String(r.quantidade),
      })),
    });
    setShowForm(true);
  };

  // T215: exclusão com o modal do sistema (antes a janela nativa do navegador)
  const [produtoParaExcluir, setProdutoParaExcluir] = useState<Product | null>(null);
  const [erroExclusao, setErroExclusao] = useState('');

  const handleDelete = (product: Product) => {
    setErroExclusao('');
    setProdutoParaExcluir(product);
  };

  const confirmarExclusao = async () => {
    const produto = produtoParaExcluir;
    if (!produto) return;
    try {
      await productService.delete(produto.id);
      setProdutoParaExcluir(null);
      await buscarProdutos();
    } catch (error) {
      const msg = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setErroExclusao(msg || 'Erro ao excluir produto');
      setProdutoParaExcluir(null);
    }
  };

  const handleArquivoImagem = async (arquivo: File) => {
    if (!arquivo.type.startsWith('image/')) {
      alert('Selecione um arquivo de imagem (JPG, PNG ou WEBP).');
      return;
    }
    setEnviandoImagem(true);
    try {
      // Foto de celular chega com ~4000px; redimensiona para 1200px (peso OK pro cardápio)
      const dataUrl = await redimensionarImagem(arquivo, 1200, 0.78);
      const res = await uploadService.enviarImagem(dataUrl);
      setFormData({ ...formData, image_url: res.data.data.url });
    } catch (error) {
      console.error('Upload falhou:', error);
      alert('Erro ao enviar a imagem. Tente novamente.');
    } finally {
      setEnviandoImagem(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ── Histórico do produto (modal) ─────────────────────────────────────
  const abrirHistorico = (product: Product) => {
    setProdutoHistorico(product);
    setBaixaForm({ quantidade: '1', motivo: 'Perda' });
    setMovPage(1);
  };

  // Busca paginada das movimentações do produto aberto no modal
  useEffect(() => {
    if (!produtoHistorico) return;
    let cancelado = false;
    estoqueService
      .movimentacoes({ page: movPage, limit: movLimit, produto_id: produtoHistorico.id })
      .then((res) => {
        if (cancelado) return;
        setMovs(res.data.data);
        syncMovs(res.data);
      })
      .catch((error) => console.error('Erro ao carregar histórico:', error));
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [produtoHistorico?.id, movPage, movLimit, movsRefresh]);

  const fazerBaixaManual = async () => {
    if (!produtoHistorico) return;
    const quantidade = Math.floor(Number(baixaForm.quantidade));
    if (!quantidade || quantidade <= 0) {
      alert('Informe uma quantidade válida.');
      return;
    }
    setBaixando(true);
    try {
      await estoqueService.baixaManual({ produto_id: produtoHistorico.id, quantidade, motivo: baixaForm.motivo });
      setMovsRefresh((v) => v + 1);
      await buscarProdutos();
      // reflete a baixa no saldo exibido no modal (o objeto do produto ainda é o antigo)
      setProdutoHistorico((prev) => (prev ? { ...prev, estoque_atual: prev.estoque_atual - quantidade } : prev));
    } catch (error) {
      alert((error as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Erro ao dar baixa.');
    } finally {
      setBaixando(false);
    }
  };

  const handleToggleAvailability = async (product: Product) => {
    try {
      await productService.update(product.id, { is_available: !product.is_available });
      await buscarProdutos();
    } catch (error) {
      console.error('Erro ao atualizar produto:', error);
    }
  };

  // Helpers da receita (ingredientes consumidos na venda)
  const adicionarReceita = () =>
    setFormData({ ...formData, receita: [...formData.receita, { insumo_id: '', quantidade: '' }] });
  const removerReceita = (idx: number) =>
    setFormData({ ...formData, receita: formData.receita.filter((_, i) => i !== idx) });
  const atualizarReceita = (idx: number, campo: Partial<{ insumo_id: string; quantidade: string }>) =>
    setFormData({
      ...formData,
      receita: formData.receita.map((r, i) => (i === idx ? { ...r, ...campo } : r)),
    });
  // Ingredientes possíveis: vêm de BUSCA no catálogo inteiro (T122) — antes só
  // enxergava os produtos da página atual da listagem
  const [ingredientesBusca, setIngredientesBusca] = useState('');
  const [ingredientesOpcoes, setIngredientesOpcoes] = useState<Product[]>([]);

  useEffect(() => {
    if (!showForm) return;
    let ativo = true;
    const timer = setTimeout(async () => {
      try {
        const r = await productService.findAll({
          busca: ingredientesBusca.trim() || undefined,
          limit: 100,
          controlar_estoque: true,
        });
        if (!ativo) return;
        setIngredientesOpcoes(r.data.data.filter((p) => p.id !== editingProduct?.id));
      } catch {
        // sem resultado da busca: mantém a lista anterior
      }
    }, 300);
    return () => {
      ativo = false;
      clearTimeout(timer);
    };
  }, [showForm, ingredientesBusca, editingProduct?.id]);

  // O ingrediente já escolhido na ficha continua visível mesmo fora do filtro
  const ingredientesDisponiveis = (() => {
    const lista = [...ingredientesOpcoes];
    for (const linha of formData.receita) {
      if (!linha.insumo_id || lista.some((p) => p.id === linha.insumo_id)) continue;
      const achado =
        products.find((p) => p.id === linha.insumo_id) ??
        ingredientesOpcoes.find((p) => p.id === linha.insumo_id);
      if (achado) lista.unshift(achado);
    }
    return lista;
  })();

  if (isLoading) {
    return <div className="text-center py-12">Carregando...</div>;
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-3xl font-bold text-gray-100">🍽️ Produtos</h1>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={buscaDigito}
            onChange={(e) => setBuscaDigito(e.target.value)}
            placeholder="🔍 Buscar produto..."
            className="input-field w-56"
          />
          <button
            onClick={() => {
              setShowForm(true);
              setEditingProduct(null);
              setFormData(formDataVazio);
            }}
            className="btn-primary"
          >
            ➕ Novo Produto
          </button>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="card w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden p-0">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700 shrink-0">
              <h2 className="text-xl font-bold text-gray-100">
                {editingProduct ? '✏️ Editar produto' : '➕ Novo produto'}
              </h2>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingProduct(null);
                }}
                className="text-gray-400 hover:text-gray-200 text-xl leading-none"
                aria-label="Fechar"
              >
                ✕
              </button>
            </div>
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            <div className="px-5 py-4 overflow-y-auto flex-1 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Nome *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Preço *</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Categoria *</label>
                <select
                  required
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                  className="input-field"
                >
                  <option value="">Selecione...</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Tipo *</label>
                <select
                  value={formData.tipo}
                  onChange={(e) => setFormData({ ...formData, tipo: e.target.value as 'PRODUTO' | 'INSUMO' })}
                  className="input-field"
                >
                  <option value="PRODUTO">Produto (vai ao cardápio)</option>
                  <option value="INSUMO">Insumo (só estoque — não vende)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Unidade</label>
                <input
                  type="text"
                  value={formData.unidade}
                  onChange={(e) => setFormData({ ...formData, unidade: e.target.value })}
                  className="input-field"
                  placeholder="un, g, ml, kg..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Foto do produto</label>
                <div className="flex items-center gap-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => {
                      const arquivo = e.target.files?.[0];
                      if (arquivo) void handleArquivoImagem(arquivo);
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={enviandoImagem}
                    className="px-3 py-2 text-xs font-medium rounded-md bg-primary-600 hover:bg-primary-500 text-white disabled:opacity-50"
                  >
                    {enviandoImagem ? 'Enviando...' : '📷 Enviar foto / tirar foto no celular'}
                  </button>
                  {formData.image_url && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, image_url: '' })}
                      className="text-xs text-red-400 hover:text-red-300"
                    >
                      Remover foto
                    </button>
                  )}
                </div>
                {formData.image_url && (
                  <img
                    src={resolverImagemUrl(formData.image_url)}
                    alt="Prévia do produto"
                    className="mt-2 h-24 w-24 rounded-lg object-cover border border-gray-700"
                  />
                )}
                <p className="text-xs text-gray-500 mt-1">
                  No celular abre a câmera na hora. Ou cole uma URL abaixo.
                </p>
                <input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="input-field mt-1"
                  placeholder="https://... (opcional)"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Descrição</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input-field"
                rows={3}
              />
            </div>
            <div className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-lg p-3">
              <input
                type="checkbox"
                id="exige-cozinha"
                checked={formData.exige_cozinha}
                onChange={(e) => setFormData({ ...formData, exige_cozinha: e.target.checked })}
                className="w-5 h-5 accent-orange-500"
              />
              <label htmlFor="exige-cozinha" className="text-sm font-medium text-gray-200">
                Preparado na cozinha
              </label>
              <span className="text-xs text-gray-500">
                — desmarque para bebidas e itens de balcão (não passam pela fila da cozinha)
              </span>
            </div>
            {formData.tipo === 'PRODUTO' && (
              <div className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-lg p-3">
                <input
                  type="checkbox"
                  id="exibir-cardapio"
                  checked={formData.exibir_cardapio}
                  onChange={(e) => setFormData({ ...formData, exibir_cardapio: e.target.checked })}
                  className="w-5 h-5 accent-orange-500"
                />
                <label htmlFor="exibir-cardapio" className="text-sm font-medium text-gray-200">
                  Exibir no cardápio
                </label>
                <span className="text-xs text-gray-500">
                  — desmarque para o produto não aparecer para venda (nem no cardápio, nem no painel)
                </span>
              </div>
            )}
            <div className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-lg p-3">
              <input
                type="checkbox"
                id="controlar-estoque"
                checked={formData.tipo === 'INSUMO' ? true : formData.controlar_estoque}
                disabled={formData.tipo === 'INSUMO'}
                onChange={(e) => setFormData({ ...formData, controlar_estoque: e.target.checked })}
                className="w-5 h-5 accent-orange-500"
              />
              <label htmlFor="controlar-estoque" className="text-sm font-medium text-gray-200">
                Controlar estoque
              </label>
              <span className="text-xs text-gray-500">
                {formData.tipo === 'INSUMO'
                  ? '— insumos sempre têm controle de estoque'
                  : '— venda dá baixa automática e saldo zero esgota o produto'}
              </span>
            </div>
            {(formData.controlar_estoque || formData.tipo === 'INSUMO') && (
              <div className="flex items-center gap-3 bg-gray-800 border border-gray-700 rounded-lg p-3">
                <div className="w-28">
                  <label className="block text-xs font-medium text-gray-400 mb-1">Consumo por venda</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={formData.consumo_por_venda}
                    onChange={(e) => setFormData({ ...formData, consumo_por_venda: e.target.value })}
                    className="input-field"
                  />
                </div>
                <div className="w-28">
                  <label className="block text-xs font-medium text-gray-400 mb-1">Estoque mínimo</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={formData.estoque_minimo}
                    onChange={(e) => setFormData({ ...formData, estoque_minimo: e.target.value })}
                    className="input-field"
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Consumo por venda: quanto sai do estoque a cada 1 unidade vendida (ex.: picanha em gramas → 300).
                  Estoque mínimo: abaixo disso, o produto entra na lista de compras sugerida.
                </p>
              </div>
            )}
            {formData.tipo === 'PRODUTO' && (
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-200">🧾 Receita (ingredientes)</span>
                  <span className="text-xs text-gray-500">consumidos automaticamente a cada venda</span>
                </div>
                {formData.receita.length === 0 && (
                  <p className="text-xs text-gray-500 mb-2">
                    Sem receita — este produto não consome estoque de ingredientes ao vender.
                  </p>
                )}
                {/* T122: busca no catálogo inteiro, não só na página da listagem */}
                <input
                  type="text"
                  value={ingredientesBusca}
                  onChange={(e) => setIngredientesBusca(e.target.value)}
                  className="input-field text-sm mb-2"
                  placeholder="🔍 Buscar ingrediente no catálogo (nome do insumo)"
                />
                {ingredientesOpcoes.length === 0 && (
                  <p className="text-xs text-amber-400 mb-2">
                    Nenhum item com controle de estoque encontrado —
                    cadastre o insumo em Produtos (tipo Insumo) para usá-lo aqui.
                  </p>
                )}
                {formData.receita.map((r, idx) => (
                  <div key={idx} className="flex items-center gap-2 mb-2">
                    <select
                      value={r.insumo_id}
                      onChange={(e) => atualizarReceita(idx, { insumo_id: e.target.value })}
                      className="input-field flex-1"
                    >
                      <option value="">Ingrediente...</option>
                      {ingredientesDisponiveis.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} ({p.tipo === 'INSUMO' ? 'insumo' : 'produto'} — {p.estoque_atual} {p.unidade})
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={r.quantidade}
                      onChange={(e) => atualizarReceita(idx, { quantidade: e.target.value })}
                      className="input-field w-24"
                      placeholder="Qtd"
                    />
                    <button
                      type="button"
                      onClick={() => removerReceita(idx)}
                      className="text-red-400 hover:text-red-300 text-sm"
                      aria-label="Remover ingrediente"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button type="button" onClick={adicionarReceita} className="text-primary-300 hover:text-primary-200 text-sm font-medium">
                  + Adicionar ingrediente
                </button>
                {formData.receita.some((r) => r.insumo_id) && (
                  <div className="mt-2 mb-2 bg-gray-900/60 rounded-lg p-2.5 text-xs text-gray-300">
                    {(() => {
                      const custo = formData.receita.reduce((acc, r) => {
                        const p = products.find((x) => x.id === r.insumo_id);
                        return acc + (p ? Number(r.quantidade || 0) * Number(p.custo_medio || 0) : 0);
                      }, 0);
                      const preco = Number(formData.price || 0);
                      const margem = preco - custo;
                      const pct = preco > 0 ? Math.round((margem / preco) * 100) : 0;
                      return (
                        <span>
                          🧾 <b>Ficha técnica:</b> custo da receita {fmtDinheiro(custo)} · margem{' '}
                          <span className={margem >= 0 ? 'text-green-400' : 'text-red-400'}>
                            {fmtDinheiro(margem)} ({pct}%)
                          </span>
                        </span>
                      );
                    })()}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  Vale usar insumos (arroz, feijão...) ou produtos do cardápio com controle de estoque — ex.: a
                  própria picanha, vendida pura ou como ingrediente do prato. A quantidade é na unidade do
                  ingrediente (300 = 300 no estoque dele).
                </p>
              </div>
            )}
            </div>
            <div className="border-t border-gray-700 px-5 py-4 shrink-0">
              {erroSalvar && (
                <p className="text-sm text-red-400 mb-3" role="alert">
                  {erroSalvar}
                </p>
              )}
              <div className="flex gap-2">
              <button type="submit" className="btn-primary">
                {editingProduct ? 'Atualizar' : 'Criar'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingProduct(null);
                }}
                className="btn-secondary"
              >
                Cancelar
              </button>
              </div>
            </div>
          </form>
          </div>
        </div>
      )}

      {/* ── LISTAGEM PAGINADA ──────────────────────────────────────────── */}
      <PaginatedContent
        loading={isLoading}
        searched={!isLoading || products.length > 0}
        hasResults={products.length > 0}
        emptyMessage="Aguarde, carregando..."
        noResultsMessage={
          buscaDigito ? 'Nenhum produto encontrado' : 'Nenhum produto cadastrado'
        }
        onClearFilters={() => setFilters({ busca: '' })}
        loadingMessage="Carregando produtos..."
      >
      {/* ── LISTA COMPACTA (CELULAR) ─────────────────────────────────── */}
      <div className="md:hidden space-y-2 mb-6">
        {products.map((product) => (
          <div key={product.id} className="card p-3 flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-gray-800 border border-gray-700 overflow-hidden shrink-0 flex items-center justify-center text-xl">
              {product.image_url ? (
                <img src={resolverImagemUrl(product.image_url)} alt="" className="w-full h-full object-cover" />
              ) : (
                '🍽️'
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-100 truncate">
                {product.name}
                {product.tipo === 'INSUMO' && (
                  <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-900/60 text-purple-300 align-middle">
                    Insumo
                  </span>
                )}
              </p>
              <p className="text-xs text-gray-400 truncate">{product.category?.name}</p>
              <div className="flex flex-wrap items-center gap-1.5 mt-1">
                {product.controlar_estoque && (
                  <>
                    <span
                      className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        product.estoque_atual > 0
                          ? 'bg-blue-900/60 text-blue-300'
                          : 'bg-red-900/60 text-red-300'
                      }`}
                    >
                      {product.estoque_atual} {product.unidade || 'un'}
                    </span>
                    {product.estoque_minimo > 0 &&
                      product.estoque_atual <= product.estoque_minimo && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-800/70 text-amber-200">
                          repor
                        </span>
                      )}
                  </>
                )}
                {product.tipo === 'INSUMO' ? (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-900/60 text-purple-300">
                    Não vende
                  </span>
                ) : product.controlar_estoque ? (
                  <span
                    className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      product.is_available
                        ? 'bg-green-900/60 text-green-300'
                        : 'bg-red-900/60 text-red-300'
                    }`}
                  >
                    {product.is_available ? 'Disponível' : 'Esgotado'}
                  </span>
                ) : (
                  <button
                    onClick={() => handleToggleAvailability(product)}
                    className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      product.is_available
                        ? 'bg-green-900/60 text-green-300'
                        : 'bg-red-900/60 text-red-300'
                    }`}
                  >
                    {product.is_available ? 'Disponível' : 'Indisponível'}
                  </button>
                )}
              </div>
            </div>
            <div className="text-right shrink-0">
              <p className="font-bold text-primary-400">
                {product.tipo === 'INSUMO' ? (
                  <span className="text-xs text-gray-500 font-normal">—</span>
                ) : (
                  `R$ ${Number(product.price).toFixed(2)}`
                )}
              </p>
              <div className="flex items-center justify-end gap-2 mt-1.5">
                {product.controlar_estoque && (
                  <button
                    onClick={() => void abrirHistorico(product)}
                    title="Histórico de estoque"
                    className="text-xs text-gray-400 hover:text-primary-300"
                  >
                    📜
                  </button>
                )}
                <button
                  onClick={() => handleEdit(product)}
                  title="Editar"
                  className="text-primary-400 hover:text-primary-300 text-sm"
                >
                  ✏️
                </button>
                <button
                  onClick={() => handleDelete(product)}
                  title="Excluir"
                  className="text-red-400 hover:text-red-300 text-sm"
                >
                  🗑️
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="hidden md:block card">
        <div className="overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead>
            <tr className="border-b">
              <th className="text-left py-3 px-4">Nome</th>
              <th className="text-left py-3 px-4">Categoria</th>
              <th className="text-left py-3 px-4">Preço</th>
              <th className="text-left py-3 px-4">Custo</th>
              <th className="text-left py-3 px-4">Estoque</th>
              <th className="text-left py-3 px-4">Status</th>
              <th className="text-right py-3 px-4">Ações</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product) => (
              <tr key={product.id} className="border-b hover:bg-gray-700/50">
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2.5">
                    {product.image_url && (
                      <img
                        src={resolverImagemUrl(product.image_url)}
                        alt=""
                        className="w-9 h-9 rounded-lg object-cover border border-gray-700 shrink-0"
                      />
                    )}
                    <div>
                      {product.name}
                      {product.tipo === 'INSUMO' && (
                        <span className="ml-2 px-2 py-0.5 rounded text-[10px] font-medium bg-purple-900/60 text-purple-300">
                          Insumo
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="py-3 px-4">{product.category?.name}</td>
                <td className="py-3 px-4">
                  {product.tipo === 'INSUMO' ? (
                    <span className="text-gray-600">—</span>
                  ) : (
                    `R$ ${Number(product.price).toFixed(2)}`
                  )}
                </td>
                <td className="py-3 px-4">
                  {product.controlar_estoque ? (
                    <span className="text-gray-300">{fmtDinheiro(product.custo_medio)}</span>
                  ) : (
                    <span className="text-gray-600">—</span>
                  )}
                </td>
                <td className="py-3 px-4">
                  {product.controlar_estoque ? (
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          product.estoque_atual > 0
                            ? 'bg-blue-900/60 text-blue-300'
                            : 'bg-red-900/60 text-red-300'
                        }`}
                      >
                        {product.estoque_atual} {product.unidade || 'un'}
                      </span>
                      {product.estoque_minimo > 0 && product.estoque_atual <= product.estoque_minimo && (
                        <span className="px-2 py-1 rounded text-xs font-medium bg-amber-800/70 text-amber-200">
                          repor
                        </span>
                      )}
                      <button
                        onClick={() => void abrirHistorico(product)}
                        title="Ver histórico de estoque"
                        className="text-xs text-gray-400 hover:text-primary-300 underline underline-offset-2"
                      >
                        histórico
                      </button>
                    </div>
                  ) : (
                    <span className="px-2 py-1 rounded text-xs font-medium bg-gray-800 text-gray-500">
                      Sem controle
                    </span>
                  )}
                </td>
                <td className="py-3 px-4">
                  {product.tipo === 'INSUMO' ? (
                    <span className="px-2 py-1 rounded text-xs font-medium bg-purple-900/60 text-purple-300">
                      Não vende
                    </span>
                  ) : product.controlar_estoque ? (
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        product.is_available
                          ? 'bg-green-900/60 text-green-300'
                          : 'bg-red-900/60 text-red-300'
                      }`}
                    >
                      {product.is_available ? 'Disponível' : 'Esgotado'}
                    </span>
                  ) : (
                    <button
                      onClick={() => handleToggleAvailability(product)}
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        product.is_available
                          ? 'bg-green-900/60 text-green-300'
                          : 'bg-red-900/60 text-red-300'
                      }`}
                    >
                      {product.is_available ? 'Disponível' : 'Indisponível'}
                    </button>
                  )}
                </td>
                <td className="py-3 px-4 text-right whitespace-nowrap space-x-2">
                  <button
                    onClick={() => handleEdit(product)}
                    className="text-primary-400 hover:text-primary-300"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => handleDelete(product)}
                    className="text-red-400 hover:text-red-300"
                  >
                    Excluir
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <PaginationControls
        currentPage={page}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={limit}
        onPageChange={setPage}
        onLimitChange={setLimit}
        label="produtos"
      />
      </PaginatedContent>

      {/* ── MODAL: HISTÓRICO DO PRODUTO ───────────────────────────────── */}
      {produtoHistorico && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={() => setProdutoHistorico(null)}
        >
          <div
            className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-bold text-gray-100">{produtoHistorico.name}</h2>
              <button onClick={() => setProdutoHistorico(null)} className="text-gray-500 hover:text-gray-300">✕</button>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm mb-4">
              <span className={`px-3 py-1.5 rounded-lg font-medium whitespace-nowrap ${produtoHistorico.estoque_atual > 0 ? 'bg-blue-900/60 text-blue-300' : 'bg-red-900/60 text-red-300'}`}>
                Saldo: {produtoHistorico.estoque_atual} {produtoHistorico.unidade || 'un'}
              </span>
              {produtoHistorico.estoque_minimo > 0 && (
                <span className="text-xs text-gray-400 whitespace-nowrap">mínimo {produtoHistorico.estoque_minimo} {produtoHistorico.unidade || 'un'}</span>
              )}
              <span className="text-xs text-gray-400 whitespace-nowrap">custo médio {fmtDinheiro(produtoHistorico.custo_medio)}/{produtoHistorico.unidade || 'un'}</span>
            </div>

            {/* Baixa manual */}
            <div className="bg-gray-800/60 rounded-lg p-3 mb-4">
              <p className="text-xs font-medium text-gray-300 mb-2">Baixa manual (perda, validade, quebra)</p>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <select
                  value={baixaForm.motivo}
                  onChange={(e) => setBaixaForm((f) => ({ ...f, motivo: e.target.value }))}
                  className="input-field w-full sm:w-40"
                >
                  <option value="Perda">Perda</option>
                  <option value="Validade">Validade vencida</option>
                  <option value="Quebra">Quebra</option>
                </select>
                <input
                  type="number" min={1} step={1} placeholder="Qtd"
                  value={baixaForm.quantidade}
                  onChange={(e) => setBaixaForm((f) => ({ ...f, quantidade: e.target.value }))}
                  className="input-field w-full sm:w-24"
                />
                <button
                  onClick={() => void fazerBaixaManual()}
                  disabled={baixando}
                  className="px-3 py-2 text-xs rounded-md bg-red-800 hover:bg-red-700 disabled:opacity-50 text-white whitespace-nowrap"
                >
                  {baixando ? 'Baixando...' : 'Registrar baixa'}
                </button>
              </div>
            </div>

            {/* Movimentações */}
            <p className="text-xs font-medium text-gray-400 mb-2">Histórico de movimentações</p>
            <div className="space-y-1">
              {movs.length === 0 && (
                <p className="text-xs text-gray-500 py-4 text-center">Sem movimentações registradas.</p>
              )}
              {movs.map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-3 bg-gray-800/40 rounded-lg px-3 py-2 text-xs">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className={`font-bold ${m.tipo === 'ENTRADA' ? 'text-green-400' : 'text-red-400'}`}
                    >
                      {m.tipo === 'ENTRADA' ? '↓ +' : '↑ −'}
                    </span>
                    <span className="text-gray-300 font-medium">
                      {m.quantidade} {produtoHistorico.unidade || 'un'}
                    </span>
                    <span className="text-gray-400 truncate">{m.motivo || ''}</span>
                  </div>
                  <span className="text-gray-500 whitespace-nowrap">
                    {new Date(m.criado_em).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              ))}
            </div>

            <PaginationControls
              currentPage={movPage}
              totalPages={movTotalPages}
              totalItems={movTotal}
              itemsPerPage={movLimit}
              onPageChange={setMovPage}
              onLimitChange={setMovLimit}
              label="movimentações"
            />
          </div>
        </div>
      )}

      {erroExclusao && <p className="text-red-400 text-sm mt-3">{erroExclusao}</p>}

      {/* T215: confirmação da exclusão no modal do sistema */}
      {produtoParaExcluir && (
        <ModalConfirmacao
          titulo="Excluir produto"
          subtitulo={produtoParaExcluir.name}
          resumo={[
            `Preço: ${fmtDinheiro(produtoParaExcluir.price)}`,
            'O produto sai do cardápio do cliente.',
            'A exclusão é recusada se o produto já tiver histórico de pedido.',
          ]}
          aviso="Não tem volta."
          rotuloConfirmar="Excluir"
          onConfirmar={confirmarExclusao}
          onCancelar={() => setProdutoParaExcluir(null)}
        />
      )}
    </div>
  );
}